import { useState } from "react";
import { ArrowDownToLine, ArrowUpFromLine, SlidersHorizontal } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { StatusBadge, EmptyState } from "@/components/brand";
import { money, dateTime } from "@/lib/format";

const TypeCell = ({ t }) => {
  const dep = t.type === "deposit";
  const Icon = dep ? ArrowDownToLine : t.type === "withdrawal" ? ArrowUpFromLine : SlidersHorizontal;
  return <span className={`flex items-center gap-2 font-semibold capitalize ${dep ? "text-cr-success" : t.type === "withdrawal" ? "text-cr-danger" : "text-cr-gold"}`}><Icon className="h-4 w-4" />{t.type}</span>;
};

const sign = (t) => (t.type === "deposit" || (t.type === "adjustment" && t.amount > 0) ? "+ " : "- ");

export const TxDetails = ({ tx, onClose, extra }) => (
  <Dialog open={!!tx} onOpenChange={(o) => !o && onClose()}>
    <DialogContent data-testid="tx-details-modal" className="max-w-lg border-cr-line bg-cr-surface text-cr-ink">
      <DialogHeader><DialogTitle>Transaction details</DialogTitle></DialogHeader>
      {tx && (
        <div className="space-y-3 text-sm">
          {[["Transaction ID", tx.reference], ["Type", tx.type], ["Amount", money(Math.abs(tx.amount))], ["Fee", money(tx.fee)],
            [tx.type === "withdrawal" ? "Net payout" : "Total", money(Math.abs(tx.total))], ["Method", tx.method_name], ["Payout to", tx.payout_details],
            ["Provider ref", tx.provider_ref], ["Reconciliation", tx.reconciliation], ["Created", dateTime(tx.created_at)], ["Note", tx.admin_note || tx.note]]
            .filter(([, v]) => v !== undefined && v !== null && v !== "")
            .map(([k, v]) => <div key={k} className="flex justify-between gap-4 border-b border-cr-line pb-2"><span className="text-cr-muted">{k}</span><span className="text-right capitalize-first">{v}</span></div>)}
          <div className="flex justify-between"><span className="text-cr-muted">Status</span><StatusBadge status={tx.status} testid="tx-details-status" /></div>
          {tx.risk_flags?.length > 0 && <p className="text-xs text-cr-danger">Risk flags: {tx.risk_flags.join(", ")}</p>}
          <div>
            <p className="mb-2 text-xs uppercase text-cr-muted">Status history</p>
            {(tx.history || []).map((h, i) => <p key={i} className="text-xs text-cr-ink/75">{dateTime(h.at)} · <b className="capitalize">{h.status}</b> · {h.by}{h.note ? ` · ${h.note}` : ""}</p>)}
          </div>
          {extra}
        </div>
      )}
    </DialogContent>
  </Dialog>
);

export default function TxTable({ items, onView, showUser = false, testid = "tx-table" }) {
  if (!items.length) return <EmptyState testid={`${testid}-empty`} title="No transactions yet" text="Your deposits and withdrawals will appear here." />;
  return (
    <>
      <div className="hidden overflow-x-auto rounded-xl border border-cr-line md:block">
        <table data-testid={testid} className="w-full text-sm">
          <thead className="bg-cr-elevated text-left text-xs text-cr-muted">
            <tr>{["Transaction ID", ...(showUser ? ["User"] : []), "Type", "Amount", "Method", "Date & Time", "Status", "Action"].map((h) => <th key={h} className="px-4 py-3 font-medium">{h}</th>)}</tr>
          </thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id} data-testid={`tx-row-${t.reference}`} className="border-t border-cr-line hover:bg-white/[0.02]">
                <td className="px-4 py-3 font-mono text-xs">{t.reference}</td>
                {showUser && <td className="px-4 py-3 text-xs">{t.user_email || t.user_id}</td>}
                <td className="px-4 py-3"><TypeCell t={t} /></td>
                <td className={`px-4 py-3 font-semibold ${sign(t) === "+ " ? "text-cr-success" : "text-cr-danger"}`}>{sign(t)}{money(Math.abs(t.amount))}</td>
                <td className="px-4 py-3 text-cr-ink/80">{t.method_name}</td>
                <td className="px-4 py-3 text-cr-ink/80">{dateTime(t.created_at)}</td>
                <td className="px-4 py-3"><StatusBadge status={t.status} testid={`tx-status-${t.reference}`} /></td>
                <td className="px-4 py-3"><button data-testid={`tx-view-${t.reference}`} onClick={() => onView(t)} className="text-cr-gold hover:underline">View</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <div className="space-y-3 md:hidden">
        {items.map((t) => (
          <button key={t.id} data-testid={`tx-card-${t.reference}`} onClick={() => onView(t)} className="card-cr flex w-full items-center justify-between p-4 text-left">
            <div><TypeCell t={t} /><p className="mt-1 font-mono text-[11px] text-cr-muted">{t.reference}</p><p className="text-xs text-cr-muted">{dateTime(t.created_at)}</p></div>
            <div className="text-right"><p className="font-semibold">{sign(t)}{money(Math.abs(t.amount))}</p><div className="mt-1"><StatusBadge status={t.status} /></div></div>
          </button>
        ))}
      </div>
    </>
  );
}
